import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMExportOutput,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {ReactNode} from 'react';

import styles from './EnhancedHeadingNode.module.css';
import {WordCountWatcher} from './WordCountWatcher';

export type SerializedWordCountNode = Spread<
  {
    headingId: string;
    className?: string;
  },
  SerializedLexicalNode
>;

export class WordCountDecoratorNode extends DecoratorNode<ReactNode> {
  __headingId: string;
  __className?: string;

  static getType(): string {
    return 'WordCount';
  }

  static clone(node: WordCountDecoratorNode): WordCountDecoratorNode {
    return new WordCountDecoratorNode(node.__headingId, node.__className, node.__key);
  }

  constructor(headingId: string, className?: string, key?: NodeKey) {
    super(key);
    this.__headingId = headingId;
    this.__className = className;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-word-count', 'true');
    element.classList.add(styles.wordCount);
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    // Return null to exclude from copy/paste operations
    // This is the proper Lexical way to exclude content from copy/paste
    return {element: null};
  }

  static importDOM(): null {
    // Don't import word count nodes from external content
    return null;
  }

  static importJSON(serializedNode: SerializedWordCountNode): WordCountDecoratorNode {
    const {headingId, className} = serializedNode;
    return $createWordCountDecoratorNode(headingId, className);
  }

  exportJSON(): SerializedWordCountNode {
    return {
      headingId: this.__headingId,
      className: this.__className,
      type: 'WordCount',
      version: 1,
    };
  }

  getTextContent(): string {
    // Return empty string so it doesn't affect text content
    return '';
  }

  isKeyboardSelectable(): boolean {
    // Make this node non-navigable with keyboard
    return false;
  }

  isIsolated(): boolean {
    // Make this node behave as a single, indivisible unit
    // This prevents the cursor from entering the node during navigation
    return true;
  }

  isInline(): boolean {
    // Make this node inline so it doesn't create line breaks
    return true;
  }

  getHeadingId(): string {
    return this.__headingId;
  }

  getClassName(): string | undefined {
    return this.__className;
  }

  setClassName(className: string | undefined): void {
    const writable = this.getWritable();
    writable.__className = className;
  }

  component(): ReactNode {
    // This will be handled by the plugin that renders the actual word count
    return null;
  }

  decorate(): ReactNode {
    return <WordCountWatcher headingId={this.__headingId} className={this.__className} />;
  }
}

export function $createWordCountDecoratorNode(
  headingId: string,
  className?: string
): WordCountDecoratorNode {
  return $applyNodeReplacement(new WordCountDecoratorNode(headingId, className));
}

export function $isWordCountDecoratorNode(node: unknown): node is WordCountDecoratorNode {
  return node instanceof WordCountDecoratorNode;
}
